import React, { useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAuth, updatePassword } from "firebase/auth";
import Routes from "../../../utils/Routes";
import Logo from "../../../assets/images/Logo.png";
import Lock from "../../../assets/icons/Lock.png";
import { Unlock } from "react-feather";
import TextBox from "../../../components/TextField";
import Button from "../../../components/Button";
import Alert from "../../../components/Alert";

function CreateNewPasswordScreen({ className }) {
  const navigate = useNavigate();
  const auth = getAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const hasSpecialSymbol = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\\/-]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasAlphabet = /[A-Za-z]/.test(password);

  const isValidPassword = (value) => {
    const passwordReg =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^-_&*])[A-Za-z\d!@#$%^&*-_]+$/;
    return passwordReg.test(value);
  };

  const updatePasswordHandler = async () => {
    if (password.length < 8) {
      setAlert({
        open: true,
        message: "Password should be at least 8 characters long",
        severity: "warning",
      });
      return;
    }
    if (!isValidPassword(password)) {
      setAlert({
        open: true,
        message: "Enter a valid password",
        severity: "warning",
      });
      return;
    }
    if (password !== confirmPassword) {
      setAlert({
        open: true,
        message: "Both passwords do not match!",
        severity: "warning",
      });
      return;
    }

    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, password);
        setAlert({
          open: true,
          message: "Password updated successfully!",
          severity: "success",
        });
        navigate("/" + Routes.LogIn);
      } else {
        setAlert({
          open: true,
          message: "User is not logged in",
          severity: "warning",
        });
      }
    } catch (error) {
      setAlert({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  return (
    <div className={className}>
      <Box className="container">
        <Box className="headingContainer">
          <img src={Logo} alt="Logo" className="logo" />
          <Typography variant="h4" className="heading">
            Create New Password
          </Typography>
        </Box>

        <Box className="form">
          <TextBox
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value.slice(0, 16))}
            icon={
              showPassword ? (
                <Unlock color={"rgba(127, 168, 135, 1)"} size={24} />
              ) : (
                Lock
              )
            }
            onIconClick={() => setShowPassword(!showPassword)}
          />
          <Typography variant="caption" color="textSecondary">
            Password must be at least 8 characters and contain
            <span style={{ color: hasNumber ? "#797979" : "red" }}>
              {" "}
              1 number
            </span>
            ,
            <span style={{ color: hasAlphabet ? "#797979" : "red" }}>
              {" "}
              1 letter
            </span>
            , and
            <span style={{ color: hasSpecialSymbol ? "#797979" : "red" }}>
              {" "}
              1 symbol
            </span>
            .
          </Typography>

          <TextBox
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value.slice(0, 16))}
            icon={
              showConfirmPassword ? (
                <Unlock color={"rgba(127, 168, 135, 1)"} size={24} />
              ) : (
                Lock
              )
            }
            onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
          />

          <Box className="buttonContainer">
            <Button
              label="Create"
              variant="contained"
              onClick={updatePasswordHandler}
            />
            <Button
              label="Cancel"
              variant="outlined"
              onClick={() => navigate("/" + Routes.LogIn)}
            />
          </Box>
        </Box>
      </Box>
      <Alert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </div>
  );
}

export default styled(CreateNewPasswordScreen)(() => ({
  ".container": {
    maxWidth: "400px",
    margin: "auto",
    padding: "20px",
  },
  ".heading": {
    fontFamily: "League Spartan",
    fontWeight: "bold",
    marginBottom: "20px",
    color: "#234000",
    marginTop: "3%",
  },
  ".form": {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  ".buttonContainer": {
    display: "flex",
    flexDirection: "column",
  },
  ".logo": {
    width: 94,
    height: 94,
  },
  ".headingContainer": {
    marginTop: "17%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));
